import { type ServiceMethod } from '@/application/interfaces/service';

export type Params = {
  cartId: string;
};

export type PaymentMethodDiscount =
  | 'bank_slip'
  | 'credit_card'
  | 'pix'
  | 'inInstallments';

export type DiscountCategory =
  | 'specialPrice'
  | 'stampedPrice'
  | 'payment-method'
  | 'stampedPrice_payment-method'
  | 'loyaltyProgram'
  | 'loyaltyProgramCashback'
  | 'employee'
  | 'promo'
  | 'twoPrices'
  | 'other';

interface Installment {
  quantity: number;
  value: number;
  withDiscount: boolean;
  total: number;
}

export interface CreditCardInstallmentsByBrand {
  brandedPro: Installment[];
  branded: Installment[];
  default: Installment[];
}
export type ItemDiscount = {
  value: number;
  percentage: number;
  origin: string;
  coupon: string | null;
  category: DiscountCategory;
  paymentMethod: PaymentMethodDiscount[];
};

export type CartSummaryData = {
  isModalitySelected: boolean;
  updatedAt: string;
  serviceTotal: number;
  discount: number;
  cashbackDiscount: number;
  freight: number;
  promiseDates?: {
    type: 'standalone-service' | 'service' | 'pickup';
    forecast: string;
  }[];
  totalDiscountsByType?: Partial<
    Record<
      Partial<PaymentMethodComplete>,
      Partial<Record<DiscountCategory, number>>
    >
  >;
  subTotal: number;
  errors?: { code: string; message: string }[];
  items: Item[];
  total: {
    inCash: number;
    inInstallments: {
      maxQuantity: number;
      installmentValue: number;
      totalValue: number;
    };
  };
  pricesByPaymentMethods: {
    bankSlip: number;
    creditCard: number;
    pix: number;
    inInstallments: number;
  };
  coupons: Record<string, number> | [];
  creditCardInstallmentsByBrand?: CreditCardInstallmentsByBrand;
};

export type Item = {
  itemId: string;
  productId: number;
  quantity: number;
  prices: Prices;
  discounts: ItemDiscount[];
};

type Prices = {
  total: Total;
  unit: Unit;
};

type Unit = {
  original: number;
  inCash: number;
  inInstallment: number;
  pricesByPaymentMethods: PricesByPaymentMethods;
};

type Total = {
  inCash: number;
  inInstallment: number;
  pricesByPaymentMethods: PricesByPaymentMethods;
};

type PricesByPaymentMethods = {
  bankSlip: number;
  creditCard: number;
  pix: number;
};

export type Response = {
  data: CartSummaryData;
};

export type QueryParams = {
  payment_method?: PaymentMethod;
  simulate: 'true';
  installments?: number;
  vouchers?: string[];
};

type PaymentMethod = 'pix' | 'credit_card' | 'bank_slip';
export type PaymentMethodComplete =
  | 'pix'
  | 'credit_card'
  | 'bank_slip'
  | 'inInstallments';

export interface Data
  extends Pick<QueryParams, 'installments' | 'vouchers'>,
    Params {
  paymentMethod?: PaymentMethod;
}

const getCartSummary: ServiceMethod<Response, Data> =
  (httpClient) => async (data, options) => {
    const { cartId, paymentMethod, installments, vouchers } = data;

    const { body: response } = await httpClient.get<
      Response,
      Params,
      QueryParams
    >('/api/v3/carts/{cartId}/summary', {
      ...options,
      searchParams: {
        payment_method: paymentMethod,
        simulate: 'true',
        installments,
        vouchers,
      },
      params: {
        cartId,
      },
    });

    return response;
  };

export default getCartSummary;
